import { useRef, type ReactElement } from 'react';
import { CHART_BAR, RADIUS } from '@vello/tokens';
import {
  RAMP_THRESHOLDS_NEUTRAL,
  RAMP_THRESHOLDS_TINT,
  bandScale,
  buildSeriesSummary,
  clamp,
  gappedBandScale,
  isFiniteNumber,
  linearScale,
  niceTicks,
  rampIndex,
  rampStepUp,
  roundCoord,
  roundedTopBarPath,
  stackSeries,
  valueDomain,
} from '@vello/charts-core';
import {
  ChartEmpty,
  ChartLegend,
  ChartLive,
  ChartLoading,
  ChartTooltip,
  cascade,
  colorOf,
  cx,
  defaultFormatValue,
  distinctTicks,
  joinSummary,
  labelStride,
  toneColor,
  useActivePoint,
  useElementWidth,
  useMotionState,
  useTweenedValues,
  type ChartToneName,
} from './kit';
import type { ChartBaseProps, LineSeries } from './line-chart';
import { ChartToolbar, TargetMark, chosenAttr, emitSelect, targetExtent, targetSummary, type ChartSelectProps, type ChartTarget } from './bi-kit';
import type { ChartCsv, ChartExportProps } from './export';

/** A column series (same shape as a line series). More than one series stacks. */
export type ColumnSeries = LineSeries;

/**
 * How the columns of a single series are filled (D549). `ramp-neutral` is the Admin A02 gray-to-ink value ramp and
 * `ramp-tint` the report-builder / Merchant navy tint ramp (the larger the column, the stronger the step); `current` paints
 * one column ink and the rest the resting gray (the driver week, the TV hour); `series` is one flat colour.
 */
export type ColumnColorBy = 'ramp-neutral' | 'ramp-tint' | 'current' | 'series';

/**
 * `design` is the Claude Design bar chart: gridless, no y-axis, no baseline, no bar stroke, a row of small x labels, bars
 * `--chart-bar-gap` apart with `--chart-bar-radius` top corners, coloured by a value ramp. `analytic` keeps a hairline grid, the
 * y-axis and a baseline for the Analytics screens (and for stacked, percent and target charts, where the axis carries meaning).
 */
export type ColumnLook = 'design' | 'analytic';

export interface ColumnChartProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  /** Default `design` for a single series without a target; `analytic` for stacked, percent or target charts. */
  readonly look?: ColumnLook;
  /** How a single series is filled (default `ramp-neutral` in the design look, `series` otherwise). */
  readonly colorBy?: ColumnColorBy;
  /** The column painted ink in `current` mode (default: the last one). */
  readonly currentIndex?: number;
  /** Bar gap in px (design look default: the `--chart-bar-gap` token, 6). */
  readonly gap?: number;
  /** Radius of the top corners in px (design look default: the `--chart-bar-radius` token, 4). */
  readonly radius?: number;
  /** The x category labels, one per column. */
  readonly categories: readonly string[];
  /** One series draws plain columns; two or more STACK, in order, from the baseline up. */
  readonly series: readonly ColumnSeries[];
  readonly height?: number;
  readonly showGrid?: boolean;
  readonly showAxis?: boolean;
  /** Print the value (the total, when stacked) above every column. The highlighted column always shows its value. */
  readonly showValues?: boolean;
  /** Paint one column in a semantic tone (a day over its limit, the worst queue). Single series only. */
  readonly highlightIndex?: number;
  readonly highlightTone?: Exclude<ChartToneName, 'neutral'>;
  /** Words naming why it is highlighted (localized); added to that column's accessible label so colour is not the only signal. */
  readonly highlightLabel?: string;
  readonly yTicks?: number;
  readonly yDomain?: readonly [number, number];
  readonly formatAxis?: (value: number) => string;
  /** When stacked, a localized name for the total row of the tooltip (omit to hide it). */
  readonly totalLabel?: string;
  /** A goal line (and an accepted band) on the value axis; its label is part of the accessible summary (D529). */
  readonly target?: ChartTarget;
  /** Two or more series draw as shares of each column's total (100 % stacked); the axis then reads percentages (D529). Value labels are not drawn. */
  readonly percent?: boolean;
  /** Format a share (a fraction 0 … 1) for the percent axis and tooltip (default: a whole percent). */
  readonly formatShare?: (fraction: number) => string;
}

const FALLBACK_WIDTH = 480;
const DESIGN_TOP = 4;
const DESIGN_TOP_WITH_VALUES = 18;
const DESIGN_X_AXIS_HEIGHT = 22;
const AXIS_CHAR_WIDTH = 6.8;
const TOP = 16;
const RIGHT = 8;
const X_AXIS_HEIGHT = 26;
const MIN_X_LABEL_GAP = 40;

function rectPath(x: number, y: number, w: number, h: number): string {
  if (!(w > 0) || !(h > 0)) return '';
  return `M${roundCoord(x)} ${roundCoord(y)}h${roundCoord(w)}v${roundCoord(h)}h${roundCoord(-w)}Z`;
}

/** Vertical columns, optionally stacked, with grid, axis, an optional highlighted column and a tooltip reachable by keyboard. */
export function ColumnChart(props: ColumnChartProps): ReactElement {
  const height = props.height ?? (resolveLook(props) === 'design' ? CHART_BAR.designHeightPx : CHART_BAR.analyticHeightPx);
  if (props.loading === true) return <ChartLoading height={height} {...(props.loadingLabel !== undefined ? { label: props.loadingLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  const count = props.categories.length;
  const hasData = count > 0 && props.series.some((s) => s.values.slice(0, count).some(isFiniteNumber));
  if (!hasData) return <ChartEmpty height={height} {...(props.emptyLabel !== undefined ? { label: props.emptyLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  return <ColumnChartPlot {...props} height={height} />;
}

function resolveLook(props: Pick<ColumnChartProps, 'look' | 'series' | 'target' | 'percent'>): ColumnLook {
  if (props.look !== undefined) return props.look;
  return props.series.length === 1 && props.target === undefined && props.percent !== true ? 'design' : 'analytic';
}

function ColumnChartPlot(props: ColumnChartProps & { height: number }): ReactElement {
  const { categories, series, height } = props;
  const format = props.formatValue ?? defaultFormatValue;
  const formatAxis = props.formatAxis ?? format;
  const look = resolveLook(props);
  const design = look === 'design';
  const showGrid = props.showGrid ?? !design;
  const showYAxis = props.showAxis ?? !design;
  const showXLabels = props.showAxis ?? true;
  const count = categories.length;
  const stacked = series.length > 1;
  const percent = props.percent === true && stacked;
  const formatShare = props.formatShare ?? ((f: number) => `${Math.round(f * 100)}%`);
  const highlight = !stacked && props.highlightIndex !== undefined && props.highlightIndex >= 0 && props.highlightIndex < count ? props.highlightIndex : undefined;
  const highlightTone = props.highlightTone ?? 'danger';
  const colorBy: ColumnColorBy = stacked ? 'series' : (props.colorBy ?? (design ? 'ramp-neutral' : 'series'));

  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const active = useActivePoint(count, props.onSelect !== undefined ? (i) => emitSelect(props, categories[i] ?? '') : undefined);

  const rawFlat = series.flatMap((s) => Array.from({ length: count }, (_, i): number | null => (isFiniteNumber(s.values[i]) ? (s.values[i] as number) : null)));
  const rawTotals = Array.from({ length: count }, (_, i) => series.reduce((sum, s) => sum + (isFiniteNumber(s.values[i]) && (s.values[i] as number) > 0 ? (s.values[i] as number) : 0), 0));
  // 100 % stacking: every column's segments become shares of that column (a column of zeros stays empty).
  const flat = percent ? rawFlat.map((v, k): number | null => (v === null ? null : (rawTotals[k % count] ?? 0) > 0 ? Math.max(0, v) / (rawTotals[k % count] ?? 1) : 0)) : rawFlat;
  const sliceOf = (all: readonly (number | null)[]): { key: string; values: readonly (number | null)[] }[] => series.map((s, si) => ({ key: s.key, values: all.slice(si * count, (si + 1) * count) }));
  const targetLayers = stackSeries(sliceOf(flat), count);
  const targetEdges = targetLayers.flatMap((l) => l.points.flatMap((p) => [p.y0, p.y1]));
  const tickFormat = (t: number): string => (percent ? formatShare(t) : formatAxis(t));
  const target = distinctTicks(
    percent
      ? niceTicks(0, 1, props.yTicks ?? 4)
      : props.yDomain !== undefined
        ? niceTicks(props.yDomain[0], props.yDomain[1], props.yTicks ?? 4)
        : valueDomain([...targetEdges, ...targetExtent(props.target)], { includeZero: true, tickCount: props.yTicks ?? 4 }),
    tickFormat,
  );
  const tweened = useTweenedValues([...flat, target.domain[0], target.domain[1]], motion);
  const layers = stackSeries(sliceOf(tweened.slice(0, flat.length)), count);
  const domain: [number, number] = [tweened[flat.length] ?? target.domain[0], tweened[flat.length + 1] ?? target.domain[1]];

  const tickText = target.ticks.map(tickFormat);
  const printsValues = !percent && ((props.showValues ?? false) || highlight !== undefined);
  const top = design ? (printsValues ? DESIGN_TOP_WITH_VALUES : DESIGN_TOP) : TOP;
  const left = showYAxis ? clamp(Math.max(...tickText.map((t) => t.length)) * AXIS_CHAR_WIDTH + 14, 30, 96) : design ? 0 : 8;
  const right = design ? 0 : RIGHT;
  const bottom = showXLabels ? (design ? DESIGN_X_AXIS_HEIGHT : X_AXIS_HEIGHT) : design ? 0 : 8;
  const plotWidth = Math.max(width - left - right, 20);
  const plotHeight = Math.max(height - top - bottom, 20);
  const baselineY = top + plotHeight;
  const y = linearScale(domain, [baselineY, top]);
  const gapPx = props.gap ?? CHART_BAR.gapPx;
  const radiusPx = props.radius ?? (design ? CHART_BAR.radiusPx : RADIUS.xs);
  const band = design ? gappedBandScale(count, [left, left + plotWidth], gapPx) : bandScale(count, [left, left + plotWidth], { padding: count > 12 ? 0.2 : 0.35 });
  const stride = labelStride(categories, band.step, MIN_X_LABEL_GAP);
  const hasNegative = domain[0] < 0;
  const maxValue = Math.max(0, ...(flat.slice(0, count).map((v) => v ?? 0)));
  const currentIndex = props.currentIndex !== undefined && props.currentIndex >= 0 && props.currentIndex < count ? props.currentIndex : count - 1;
  /** The fill of a single-series column: a value-ramp step, the ink / resting pair, or the flat series colour. */
  const fillOf = (i: number, raw: number, hover: boolean): string => {
    if (colorBy === 'ramp-neutral' || colorBy === 'ramp-tint') {
      const tint = colorBy === 'ramp-tint';
      const steps = tint ? 3 : 5;
      const base = rampIndex(raw, maxValue, tint ? RAMP_THRESHOLDS_TINT : RAMP_THRESHOLDS_NEUTRAL);
      return `var(--chart-ramp-${tint ? 'tint' : 'neutral'}-${(hover ? rampStepUp(base, steps) : base) + 1})`;
    }
    if (colorBy === 'current') return raw <= 0 ? 'var(--chart-bar-empty)' : i === currentIndex || hover ? 'var(--chart-bar-current)' : 'var(--chart-bar-rest)';
    return colorOf(0, series[0]?.tone);
  };
  const zeroY = y(0);

  const totalsAt = (i: number): number => (percent ? (rawTotals[i] ?? 0) : targetLayers.reduce((sum, l) => sum + (l.points[i]?.value ?? 0), 0));
  const topAt = (i: number): number => y(Math.max(0, ...targetLayers.map((l) => l.points[i]?.y1 ?? 0)));

  const summary =
    props.ariaLabel ??
    joinSummary([
      props.title,
      targetSummary(props.target, format),
      ...series.map((s) =>
        buildSeriesSummary(
          categories.map((label, i) => ({ label: highlight === i && props.highlightLabel !== undefined ? `${label} (${props.highlightLabel})` : label, value: s.values[i] })),
          { ...(stacked ? { title: s.label } : {}), format },
        ),
      ),
    ]);

  const activeIndex = active.index;
  const activeRows =
    activeIndex === null
      ? []
      : [
          ...series.map((s, si) => ({
            key: s.key,
            label: s.label,
            value: isFiniteNumber(s.values[activeIndex]) ? `${format(s.values[activeIndex] as number)}${percent ? ` (${formatShare(flat[si * count + activeIndex] ?? 0)})` : stacked && (rawTotals[activeIndex] ?? 0) > 0 && (s.values[activeIndex] as number) >= 0 ? ` (${formatShare((s.values[activeIndex] as number) / (rawTotals[activeIndex] ?? 1))})` : ''}` : '–',
            color: colorOf(si, s.tone),
          })),
          ...(stacked && props.totalLabel !== undefined ? [{ key: '__total', label: props.totalLabel, value: format(totalsAt(activeIndex)), color: undefined }] : []),
        ];
  const activeText = activeIndex === null ? '' : `${categories[activeIndex] ?? ''}: ${activeRows.map((r) => (series.length > 1 || r.key === '__total' ? `${r.label} ${r.value}` : r.value)).join(', ')}`;
  const valueLabelFor = (i: number): string | null => {
    if (showValuesAt(i)) return format(stacked ? totalsAt(i) : (flat[i] ?? 0));
    return null;
  };
  function showValuesAt(i: number): boolean {
    return !percent && ((props.showValues ?? false) || highlight === i);
  }
  const csv = (): ChartCsv => ({ header: ['', ...series.map((s) => s.label)], rows: categories.map((c, i) => [c, ...series.map((s) => (isFiniteNumber(s.values[i]) ? (s.values[i] as number) : null))]) });

  return (
    <div className={cx('vello-chart', 'vello-chart--columns', design && 'vello-chart--design', props.className)} data-motion={motion} data-look={look}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div ref={ref} className="vello-chart__plot vello-chart__focus" role="img" aria-label={summary} tabIndex={0} onKeyDown={active.onKeyDown} onFocus={active.onFocus} onBlur={active.onBlur}>
        <svg
          className="vello-chart__svg"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          onClick={(e) => active.activateFromPointer(e, (x) => band.indexAt(x))}
          onPointerMove={(e) => active.fromPointer(e, (x) => band.indexAt(x))}
          onPointerDown={(e) => active.fromPointer(e, (x) => band.indexAt(x))}
          onPointerLeave={active.onPointerLeave}
        >
          {props.target !== undefined && <TargetMark target={props.target} y={y} left={left} right={left + plotWidth} format={format} layer="band" />}
          {showGrid && target.ticks.map((t) => <line key={`g${t}`} className={t === 0 ? 'vello-chart__baseline' : 'vello-chart__grid'} x1={left} x2={left + plotWidth} y1={y(t)} y2={y(t)} />)}
          {showYAxis &&
            target.ticks.map((t, i) => (
              <text key={`t${t}`} className="vello-chart__tick tnum" x={left - 8} y={y(t)} dy="0.32em" textAnchor="end">
                {tickText[i]}
              </text>
            ))}
          {activeIndex !== null && !design && <rect className="vello-chart__band" x={band.start(activeIndex) - (band.step - band.bandwidth) / 2} y={top} width={band.step} height={plotHeight} />}
          {Array.from({ length: count }, (_, i) => {
            const columnLayers = layers.map((l) => l.points[i]);
            const topmostPositive = (() => {
              let found = -1;
              columnLayers.forEach((p, li) => {
                if (p !== undefined && p.value > 0) found = li;
              });
              return found;
            })();
            const isHot = highlight === i;
            return (
              <g key={`c${i}`} {...chosenAttr(props, categories[i] ?? '')}>
                {columnLayers.map((p, li) => {
                  if (p === undefined || p.value === 0) return null;
                  const yTop = Math.min(y(p.y1), y(p.y0));
                  const h = Math.abs(y(p.y0) - y(p.y1));
                  const x = band.start(i);
                  const d = p.value > 0 && li === topmostPositive ? roundedTopBarPath(x, yTop, band.bandwidth, h, Math.min(radiusPx, band.bandwidth / 2)) : rectPath(x, yTop, band.bandwidth, h);
                  const s = series[li];
                  return (
                    <path
                      key={s?.key ?? li}
                      className="vello-chart__bar vello-chart__rise"
                      data-negative={p.value < 0 ? 'true' : undefined}
                      d={d}
                      data-active={activeIndex === null ? undefined : activeIndex === i ? 'true' : 'false'}
                      fill={isHot ? toneColor(highlightTone) : stacked ? colorOf(li, s?.tone) : fillOf(i, p.value, activeIndex === i)}
                      style={cascade(i)}
                    />
                  );
                })}
                {valueLabelFor(i) !== null && (
                  <text className="vello-chart__value tnum" x={band.center(i)} y={Math.max(topAt(i) - 6, 10)} textAnchor="middle" {...(isHot ? { fill: toneColor(highlightTone) } : {})}>
                    {valueLabelFor(i)}
                  </text>
                )}
              </g>
            );
          })}
          {showXLabels &&
            categories.map((label, i) =>
              i % stride === 0 ? (
                <text key={`x${i}`} className="vello-chart__tick vello-chart__xlabel" x={band.center(i)} y={baselineY + (design ? 15 : 18)} textAnchor="middle">
                  {label}
                </text>
              ) : null,
            )}
          {(!design || hasNegative || showGrid) && <line className="vello-chart__baseline" x1={left} x2={left + plotWidth} y1={zeroY} y2={zeroY} />}
          {props.target !== undefined && <TargetMark target={props.target} y={y} left={left} right={left + plotWidth} format={format} layer="line" />}
        </svg>
        {activeIndex !== null && (
          <ChartTooltip
            x={band.center(activeIndex)}
            y={topAt(activeIndex)}
            width={width}
            heading={categories[activeIndex] ?? ''}
            rows={activeRows.map((r) => ({ key: r.key, ...(stacked || r.key === '__total' ? { label: r.label } : {}), ...(stacked && r.color !== undefined ? { color: r.color } : {}), value: r.value }))}
          />
        )}
      </div>
      <ChartLive text={activeText} />
      {stacked && <ChartLegend items={series.map((s, si) => ({ key: s.key, label: s.label, color: colorOf(si, s.tone), shape: 'bar' as const }))} />}
    </div>
  );
}
